import {
  useEffect,
  useMemo,
  useState,
  Fragment,
  ReactNode,
} from 'react';
import { styled } from '@mui/material/styles';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from './TableCell';
import TableRow from '@mui/material/TableRow';
import TableCheckbox from './TableCheckbox';
import CircularProgress from '@mui/material/CircularProgress';
import {
  getComparator,
  Order,
  ColumnDef,
  TableType,
  RowErrors,
} from './TableHelpers';
import TableHead from './TableHead';

const Table = <T extends TableType>({
  className,
  rows,
  columns,
  selectedRows,
  onSelectedRowsChange,
  order: initialOrder,
  orderBy: initialOrderBy,
  onRequestSort,
  loading = false,
  errors,
  dense = false,
  renderRowDetail,
  emptyMessage,
}: {
  className?: string;
  rows: T[];
  columns: ColumnDef<T>[];
  selectedRows?: number[];
  onSelectedRowsChange?: (selected: number[]) => void;
  order?: Order;
  orderBy?: keyof T | null;
  onRequestSort?: (property: keyof T) => void;
  loading?: boolean;
  errors?: RowErrors<T>;
  dense?: boolean;
  // Full-width row rendered beneath its own row; null for the rest.
  renderRowDetail?: (row: T) => ReactNode;
  // Shown in place of rows when there is no data and nothing is loading.
  emptyMessage?: ReactNode;
}) => {
  // Guards against null/undefined entries in rows coming from the API.
  const validRows = useMemo(() => {
    const filtered = (rows || []).filter((row): row is T =>
      Boolean(row),
    );
    if (import.meta.env.DEV && rows && filtered.length < rows.length) {
      console.warn(
        `Table: skipped ${rows.length - filtered.length} invalid row(s)`,
      );
    }
    return filtered;
  }, [rows]);
  const [order, setOrder] = useState<Order>(initialOrder || 'asc');
  const [orderBy, setOrderBy] = useState<keyof T | null>(
    initialOrderBy ||
      ((Object.keys(validRows[0] || {})?.[0] || null) as keyof T | null),
  );
  const selectionEnabled = Boolean(onSelectedRowsChange);

  useEffect(() => {
    setOrder(initialOrder || 'asc');
  }, [initialOrder]);

  useEffect(() => {
    setOrderBy(
      initialOrderBy ||
        ((Object.keys(validRows[0] || {})?.[0] || null) as
          | keyof T
          | null),
    );
  }, [initialOrderBy, validRows]);

  const handleRequestSort = (property: keyof T) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (checked: boolean) => {
    if (!selectionEnabled || !onSelectedRowsChange) {
      return;
    }

    if (checked) {
      const newSelected = validRows.map((n) => n.id);
      onSelectedRowsChange(newSelected);
      return;
    }
    onSelectedRowsChange([]);
  };

  const handleClick = (id: number) => {
    if (selectedRows === undefined || !onSelectedRowsChange) {
      return;
    }

    const selectedIndex = selectedRows.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1),
      );
    }
    onSelectedRowsChange(newSelected);
  };

  const sortedRows = useMemo(
    () =>
      onRequestSort
        ? validRows
        : [...validRows].sort(getComparator(order, orderBy)),
    [validRows, order, orderBy, onRequestSort],
  );

  const hasData = validRows.length > 0;

  return (
    <StyledTableContainer className={className} $dense={dense}>
      <TableWrapper>
        <MuiTable role="table" aria-label="Data table">
          <TableHead
            numSelected={selectedRows?.length || 0}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={onRequestSort || handleRequestSort}
            rowCount={validRows.length}
            columns={columns}
            selectionEnabled={selectionEnabled}
            dense={dense}
          />
          <TableBody>
            {sortedRows.map((row) => {
              const isItemSelected = Boolean(
                selectedRows?.includes(row.id),
              );
              const rowError = errors?.[row.id]?._row;
              const rowDetail = renderRowDetail?.(row);
              const fullWidthColSpan =
                columns.length + (selectionEnabled ? 1 : 0);

              return (
                <Fragment key={row.id}>
                  <TableRow
                    hover
                    tabIndex={-1}
                    selected={isItemSelected}
                    aria-selected={isItemSelected}
                  >
                    {selectionEnabled ? (
                      <TableCell
                        padding="checkbox"
                        dense={dense}
                        sx={{ height: dense ? 40 : 60 }}
                      >
                        <TableCheckbox
                          checked={isItemSelected}
                          onChange={() => handleClick(row.id)}
                          aria-label={`Select row ${row.id}`}
                        />
                      </TableCell>
                    ) : null}
                    {columns.map((column) => {
                      const key =
                        column.field?.toString() ||
                        column.id ||
                        'action';
                      const fieldValue = column.field
                        ? row[column.field]
                        : undefined;
                      const fieldError =
                        column.field && errors?.[row.id]
                          ? errors[row.id][column.field]
                          : undefined;

                      return (
                        <TableCell<T>
                          key={key}
                          rowId={row.id}
                          align={column.align || 'left'}
                          padding={
                            column.align === 'right'
                              ? 'none'
                              : 'normal'
                          }
                          dataField={fieldValue}
                          dataRow={row}
                          editable={column.editable}
                          onChange={column.onChange}
                          render={column.render}
                          width={column.width}
                          required={column.required}
                          error={fieldError}
                          formatError={column.formatError}
                          dense={dense}
                          sx={{ height: dense ? 40 : 60 }}
                        />
                      );
                    })}
                  </TableRow>
                  {rowError && (
                    <ErrorRow key={`${row.id}-error`}>
                      <TableCell
                        colSpan={fullWidthColSpan}
                        dense={dense}
                        sx={{ 
                          height: 'auto', 
                          padding: dense ? '6px 8px' : '8px 12px' 
                        }}
                      >
                        <ErrorMessage>{rowError}</ErrorMessage>
                      </TableCell>
                    </ErrorRow>
                  )}
                  {rowDetail && (
                    <TableRow key={`${row.id}-detail`}>
                      <TableCell
                        colSpan={fullWidthColSpan}
                        dense={dense}
                        sx={{ height: 'auto', padding: 0 }}
                      >
                        {rowDetail}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {!hasData && !loading && emptyMessage ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectionEnabled ? 1 : 0)}
                  dense={dense}
                  sx={{ height: 'auto' }}
                >
                  <EmptyMessage>{emptyMessage}</EmptyMessage>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </MuiTable>
        {loading && (
          <LoaderOverlay $hasData={hasData}>
            <LoaderWrapper role="status" aria-label="Loading data">
              <CircularProgress />
            </LoaderWrapper>
          </LoaderOverlay>
        )}
      </TableWrapper>
    </StyledTableContainer>
  );
};
const StyledTableContainer = styled('div')<{ $dense?: boolean }>(
  ({ theme, $dense }) => ({
    width: '100%',

    table: {
      tableLayout: 'fixed',
      borderTop: `1px solid ${theme.palette.grey200}`,
      borderRight: `1px solid ${theme.palette.grey200}`,
      borderLeft: `1px solid ${theme.palette.grey200}`,
    },

    tr: {
      borderBottom: `1px solid ${theme.palette.grey200}`,
    },

    'tr > th:first-of-type, tr > td:first-of-type': {
      width: 24,
      padding: $dense ? '0 8px' : '0 12px',
    },

    'tr > th:last-child, tr > td:last-child': {
      paddingRight: $dense ? 8 : 12,
    },
  }),
);

const TableWrapper = styled('div')({
  position: 'relative',
});

const LoaderOverlay = styled('div')<{ $hasData: boolean }>(
  ({ $hasData }) => ({
    position: 'absolute',
    top: '60px',
    left: 1,
    right: 1,
    bottom: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    minHeight: $hasData ? 'auto' : '150px',
    zIndex: 10,
  }),
);

const LoaderWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const EmptyMessage = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.grey500,
  fontSize: 14,
  lineHeight: '20px',
}));

const ErrorRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.red50 || '#FEF2F2',
  '&:hover': {
    backgroundColor: theme.palette.red50 || '#FEF2F2',
  },
}));

const ErrorMessage = styled('div')(({ theme }) => ({
  color: theme.palette.red500 || '#EF4444',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
}));

export default Table;
